import { Logger } from '../logger'
import { isDefined, isEmpty, isNotDefined } from '../isEmpty'
import {
  type SubtractionOperation,
  type CustomFieldData,
  type CustomFieldId,
  type AdditionOperation,
  type MultiplicationOperation,
  type DivisionOperation,
  type CustomFieldMap,
  type CustomFieldValue,
} from '../../model'
import { isCalculationCustomField } from './util'
import { toCustomFieldDisplayValue, getStringValue, getNumberValue, findDateValue } from './display'

const logger = new Logger('CustomFieldCalculator')

type ValueAndType =
  | {
      type: 'string' | 'url'
      value: string
    }
  | {
      type: 'number' | 'calculation'
      value: number | string
    }
  | {
      type: 'boolean'
      value: boolean | undefined
    }
  | {
      type: 'date'
      value?: Date
    }
  | {
      type: 'multipleChoice'
      value: string
      options: string[]
    }
  | {
      type: 'attachment'
      value: string
    }

export class CustomFieldCalculator {
  private readonly customFields: Map<CustomFieldId, CustomFieldData>
  private readonly customFieldValues: Map<CustomFieldId, CustomFieldValue | undefined>

  constructor(customFields: CustomFieldData[], values: CustomFieldMap | undefined = {}) {
    this.customFields = new Map(customFields.map((f) => [f.fieldId, f]))
    this.customFieldValues = Object.entries(values).reduce((acc, [key, value]) => {
      acc.set(key, value)
      return acc
    }, new Map())
  }

  public getDisplayValue(fieldToCalculate: CustomFieldId): string {
    const field = this.customFields.get(fieldToCalculate)
    const value = this.findCustomFieldValue(fieldToCalculate)
    if (!field) return 'N/A'

    return toCustomFieldDisplayValue(field, value)
  }

  public findCustomFieldValue(fieldToCalculate: CustomFieldId): CustomFieldValue | undefined {
    try {
      const field = this.customFields.get(fieldToCalculate)
      if (!field) return undefined
      if (isCalculationCustomField(field)) return this.findCalculatedValue(fieldToCalculate)
      const stored = this.customFieldValues.get(fieldToCalculate)
      if (field.fieldType === 'number' && !Array.isArray(stored)) return this.findNumberValue(fieldToCalculate)
      return stored
    } catch (error) {
      logger.error(`Error finding custom field value ${fieldToCalculate}`, error)
      return undefined
    }
  }

  public getValueAndType(fieldToCalculate: CustomFieldId): ValueAndType {
    const field = this.customFields.get(fieldToCalculate)
    const value = this.findCustomFieldValue(fieldToCalculate)

    switch (field?.fieldType) {
      case 'string':
        return { type: 'string', value: getStringValue(value) }
      case 'url':
        return { type: 'url', value: getStringValue(value) }
      case 'number':
        return { type: 'number', value: getNumberValue(value) }
      case 'boolean':
        return { type: 'boolean', value: typeof value === 'boolean' ? value : undefined }
      case 'date':
        return { type: 'date', value: findDateValue(value) }
      case 'calculation':
        return { type: 'calculation', value: getNumberValue(this.findCalculatedValue(fieldToCalculate)) }
      case 'multipleChoice':
        return { type: 'multipleChoice', value: getStringValue(value), options: field.options }
      case 'attachment':
        return { type: 'attachment', value: getStringValue(value) }
      default:
        return { type: 'string', value: getStringValue(value) }
    }
  }

  private findCalculatedValue(fieldToCalculate: CustomFieldId): number | undefined {
    const field = this.customFields.get(fieldToCalculate)
    if (!field) {
      logger.info(`Field not found ${fieldToCalculate}`)
      return undefined
    }
    if (!isCalculationCustomField(field)) {
      logger.info(`Field is not a calculation ${fieldToCalculate}`)
      return undefined
    }

    const { calculation } = field

    switch (calculation.operation) {
      case 'addition':
        return this.calculateAddition(calculation)
      case 'multiplication':
        return this.calculateMultiplication(calculation)
      case 'subtraction':
        return this.calculateSubtraction(calculation)
      case 'division':
        return this.calculateDivision(calculation)
      default:
        return undefined
    }
  }

  private calculateAddition({ params }: AdditionOperation): number | undefined {
    const numbersToAdd = params.map((param) => this.findNumberValue(param)).filter(isDefined)
    if (numbersToAdd.length !== params.length) return undefined
    return numbersToAdd.reduce((acc, param) => acc + param, 0)
  }

  private calculateMultiplication({ params }: MultiplicationOperation): number | undefined {
    const numbersToMultiply = params.map((param) => this.findNumberValue(param)).filter(isDefined)
    if (numbersToMultiply.length !== params.length) return undefined
    return numbersToMultiply.reduce((acc, param) => acc * param, 1)
  }

  private calculateSubtraction({ minuend, subtrahend }: SubtractionOperation): number | undefined {
    const minuendValue = this.findNumberValue(minuend)
    const subtrahendValue = this.findNumberValue(subtrahend)
    if (isNotDefined(minuendValue)) return undefined
    if (isNotDefined(subtrahendValue)) return undefined
    return minuendValue - subtrahendValue
  }

  private calculateDivision({ dividend, divisor }: DivisionOperation): number | undefined {
    const dividendValue = this.findNumberValue(dividend)
    const divisorValue = this.findNumberValue(divisor)
    if (isNotDefined(dividendValue)) return undefined
    if (isEmpty(divisorValue)) return undefined
    return dividendValue / divisorValue
  }

  private findNumberValue(param: CustomFieldId | number): number | undefined {
    if (typeof param === 'number') return param

    const value = this.customFieldValues.get(param)
    if (Array.isArray(value)) return undefined
    if (typeof value === 'number') return value
    const numberValue = Number(value)
    // Number(null) is 0, so a cleared value must fall through instead of counting as zero
    if (isDefined(value) && !isNaN(numberValue)) return numberValue
    const field = this.customFields.get(param)
    if (!field) return undefined
    if (!isCalculationCustomField(field)) return undefined
    return this.findCalculatedValue(param)
  }

  public toData(): CustomFieldMap {
    const calculatedMap = new Map<CustomFieldId, CustomFieldValue>()
    this.customFields.forEach((field) => {
      const value = this.findCustomFieldValue(field.fieldId)
      if (value !== undefined) {
        calculatedMap.set(field.fieldId, value)
      }
    })
    return Object.fromEntries(calculatedMap)
  }
}
